import * as Sentry from '@sentry/react';
import { config } from 'rxjs';

/**
 * Route RxJS's unhandled stream errors into Sentry with the raw errored value
 * attached (WA-3718 / Sentry WAWIASSIST-236).
 *
 * Why this exists: when an observable errors and the subscriber has no `error`
 * callback, RxJS 7 calls `ConsumerObserver.error` → `handleUnhandledError` →
 * `reportUnhandledError`, whose default branch is literally
 * `setTimeout(() => { throw err })` (rxjs/internal/util/reportUnhandledError).
 * That rethrow happens in a timer, so:
 *   - the stack points at the timer, not at the failing stream;
 *   - the raw value is thrown as-is, so a non-Error value (`undefined`) reaches
 *     `window.onerror` as a bare string. Firefox renders that as
 *     "uncaught exception: undefined", which Sentry can only record as a
 *     synthetic one-frame event (`/customer/general:1`) with no stack and no
 *     indication of which stream failed — unactionable by construction.
 * This codebase has ~590 `.subscribe(...)` call sites in `src/`, of which only a
 * handful use the object form that can carry an `error` callback, so the whole
 * app funnels into that default branch. `responseHandler` absorbs errors for the
 * streams it wraps (its `catchError` returns `of(model)`), which is why the
 * escape is rare rather than constant — but "rare and undiagnosable" is exactly
 * what WA-3718 was.
 *
 * Installing `config.onUnhandledError` replaces the `throw err` branch, so these
 * stop arriving as stackless `window.onerror` noise and arrive instead as a
 * grouped, tagged Sentry exception carrying the value that was thrown.
 */

const MAX_SERIALIZED_LENGTH = 200;

const truncate = (text: string): string =>
  text.length > MAX_SERIALIZED_LENGTH ? `${text.slice(0, MAX_SERIALIZED_LENGTH)}…` : text;

/**
 * Discarded notifications reported per page load, after which the detector goes
 * quiet (WA-3826).
 *
 * The detector is a smoke alarm: the first few reports say everything the tenth
 * thousand does. Left uncapped it becomes the fire — see
 * `describeStoppedPayload` for why an unbounded console stream is expensive in a
 * way an unbounded Sentry stream is not.
 */
export const MAX_STOPPED_NOTIFICATION_REPORTS = 25;

/** Top-level keys named in a payload description before it elides the rest. */
const MAX_DESCRIBED_KEYS = 8;

const KIND_LABELS: Record<string, string> = { N: 'next', E: 'error', C: 'complete' };

let stoppedNotificationCount = 0;

/**
 * Bounded, SHALLOW description of a discarded notification's payload — a type
 * and its top-level shape, never the value itself.
 *
 * Both halves of that matter, and neither is about log readability (WA-3826):
 *
 * - *Shallow.* `describeUnhandledValue` reaches for `JSON.stringify` and then
 *   truncates to 200 characters, which is fine for a thrown error value and
 *   ruinous here: the payload of a discarded `next` is a whole GraphQL response
 *   (a customer list, an order-positions grid), so the serialization work is
 *   paid in full and then thrown away. This walks own keys one level deep and
 *   stops, so cost is bounded by the top-level key count, not by response size.
 *
 * - *A string, not the object.* `console.warn(msg, obj)` is nearly free with
 *   DevTools closed and very much not free with it open: Chrome builds a
 *   `RemoteObject` preview per argument, ships it over CDP, and the DevTools
 *   frontend renders and retains it. At the rate `graphqlRequest.ts` discards
 *   notifications (every `switchMap` supersede — one per keystroke in a search
 *   field), that pegged the DevTools renderer process at ~680% CPU and froze the
 *   panel, while the page itself stayed responsive and prod stayed clean. Hence
 *   a pre-rendered string: no live reference for DevTools to preview.
 *
 * Must never throw — it runs inside `timeoutProvider.setTimeout`, where a throw
 * becomes exactly the stackless global error this module exists to remove.
 */
export function describeStoppedPayload(value: unknown): string {
  if (value === undefined) return 'undefined';
  if (value === null) return 'null';
  if (value instanceof Error) return truncate(`${value.name}: ${value.message}`);
  if (typeof value === 'string') return value === '' ? '"" (empty string)' : `"${truncate(value)}"`;
  if (typeof value !== 'object') return truncate(String(value));

  try {
    if (Array.isArray(value)) return `array(${value.length})`;
    if (value instanceof Map || value instanceof Set) {
      return `${value.constructor.name}(${value.size})`;
    }
    // Own enumerable keys only: one level, no recursion into the values.
    const keys = Object.keys(value);
    const shown = keys.slice(0, MAX_DESCRIBED_KEYS).join(', ');
    const elided =
      keys.length > MAX_DESCRIBED_KEYS ? `, …+${keys.length - MAX_DESCRIBED_KEYS}` : '';
    const name = value.constructor?.name;
    return truncate(`${!name || name === 'Object' ? 'object' : name}{${shown}${elided}}`);
  } catch {
    // A Proxy or a throwing getter reached through `Object.keys`/`constructor`.
    return `[undescribable ${typeof value}]`;
  }
}

/**
 * Stable, low-cardinality label for the thrown value — used as the Sentry
 * grouping fingerprint, so `undefined` throws from different streams stay in one
 * issue instead of fragmenting across identical timer stacks.
 */
export function unhandledValueKind(value: unknown): string {
  if (value === undefined) return 'undefined';
  if (value === null) return 'null';
  if (value instanceof Error) return `Error:${value.name}`;
  if (Array.isArray(value)) return 'array';
  return typeof value;
}

/**
 * Human-readable rendering of the thrown value for the Sentry event. Must never
 * throw: it runs on the reporting path, and a throw here would recreate the very
 * stackless global error this module exists to remove.
 */
export function describeUnhandledValue(value: unknown): string {
  if (value === undefined) return 'undefined';
  if (value === null) return 'null';
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (typeof value === 'string') return value === '' ? '"" (empty string)' : `"${truncate(value)}"`;
  if (typeof value === 'symbol' || typeof value === 'function' || typeof value === 'bigint') {
    return truncate(String(value));
  }
  try {
    const serialized = JSON.stringify(value);
    return serialized === undefined ? truncate(String(value)) : truncate(serialized);
  } catch {
    // Circular reference, a BigInt inside an object, or a throwing `toJSON`.
    return `[unserializable ${typeof value}]`;
  }
}

interface IInstallOptions {
  /**
   * Also install `config.onStoppedNotification`, which reports `next`/`error`/
   * `complete` delivered to an ALREADY-STOPPED subscriber. RxJS makes that a
   * silent no-op unless this hook is set (`handleStoppedNotification` in
   * rxjs/internal/Subscriber), so the class is invisible by default — it hides
   * genuine races such as an async `next` callback calling `subscriber.error()`
   * after `subscriber.complete()` has run. Kept out of production: it is a
   * development smoke detector, not user-facing telemetry.
   *
   * Reports are capped at `MAX_STOPPED_NOTIFICATION_REPORTS` per page load and
   * carry only a shallow description of the payload, never the notification
   * object — the uncapped, object-logging version of this hook was itself a
   * performance bug on the dev deployment (WA-3826).
   */
  reportStoppedNotifications?: boolean;
}

export function installRxJsUnhandledErrorReporting({
  reportStoppedNotifications = false,
}: IInstallOptions = {}): void {
  config.onUnhandledError = (err: unknown) => {
    try {
      const kind = unhandledValueKind(err);
      const description = describeUnhandledValue(err);

      Sentry.withScope((scope) => {
        // Distinguishes this transport from a genuine window-level throw, so
        // triage knows an RxJS stream failed rather than a render/effect.
        scope.setTag('errorTransport', 'rxjs.onUnhandledError');
        scope.setTag('rxjsUnhandledValueKind', kind);
        scope.setContext('rxjsUnhandledError', { valueKind: kind, value: description });

        if (err instanceof Error) {
          // A real Error carries its own stack and message — capture it as-is so
          // Sentry keeps both, and let default grouping apply.
          Sentry.captureException(err);
          return;
        }

        // A non-Error value has no stack of its own. Wrap it so the event has a
        // usable title and a deliberate fingerprint instead of grouping on the
        // interchangeable timer stack.
        const synthetic = new Error(`RxJS unhandled stream error: ${description}`);
        synthetic.name = 'RxJsUnhandledStreamError';
        scope.setFingerprint(['rxjs-unhandled-non-error', kind]);
        Sentry.captureException(synthetic);
      });

      // Preserve the console signal the replaced `throw err` used to give in dev.
      console.error('[rxjs] unhandled stream error:', err);
    } catch {
      // Swallow: this hook runs inside a timer, so throwing here would produce
      // exactly the stackless uncaught global error it was added to eliminate.
    }
  };

  if (reportStoppedNotifications) {
    // Installing is the start of a session, so the budget starts over with it —
    // this also keeps repeated installs (tests, a Vite HMR reload) from
    // inheriting a spent budget and reporting nothing.
    stoppedNotificationCount = 0;

    config.onStoppedNotification = (notification) => {
      try {
        stoppedNotificationCount++;
        if (stoppedNotificationCount > MAX_STOPPED_NOTIFICATION_REPORTS) {
          // Announce going quiet exactly once, so a silent detector is never
          // mistaken for a clean one.
          if (stoppedNotificationCount === MAX_STOPPED_NOTIFICATION_REPORTS + 1) {
            console.warn(
              `[rxjs] Reported ${MAX_STOPPED_NOTIFICATION_REPORTS} discarded notifications; ` +
                'further ones are not logged until the next reload. Reload with the console ' +
                'cleared to see them again.',
            );
          }
          return;
        }

        const kind = KIND_LABELS[notification.kind] ?? notification.kind;
        const payload =
          notification.kind === 'N'
            ? ` Value: ${describeStoppedPayload(notification.value)}.`
            : notification.kind === 'E'
            ? ` Error: ${describeStoppedPayload(notification.error)}.`
            : '';

        // One pre-rendered string, never the notification object — see
        // `describeStoppedPayload`.
        console.warn(
          `[rxjs] "${kind}" notification delivered to an already-stopped subscriber — ` +
            'RxJS discards it silently. Likely an async callback resolving after ' +
            `complete/unsubscribe.${payload} ` +
            `(${stoppedNotificationCount}/${MAX_STOPPED_NOTIFICATION_REPORTS})`,
        );
      } catch {
        // Swallow: same contract as `onUnhandledError` above — this runs inside
        // a timer, so throwing here would produce the stackless uncaught global
        // error the module exists to eliminate.
      }
    };
  }
}
